import { FunctionComponent, useCallback } from 'react'

import styled, { css } from 'styled-components'

import { desktopCSS } from '@measures/responsive'
import useTracking, { TrackingFnType } from '@hooks/useTracking'
import useViewportTracking from '@hooks/useViewportTracking'
import { RelatedEntitiesProps } from '@widgets/RelatedEntities'
import PrimaryCTABlink from '@components/Buttons/PrimaryCTABlink'

import EntityImage from './EntityImage'

const Wrapper = styled.div<{ hideOnDesktop?: boolean }>`
  ${({ hideOnDesktop }) => {
    return css`
      display: flex;
      flex-direction: column;
      justify-content: space-between;
      flex-wrap: wrap;
      ${desktopCSS(css`
        display: ${hideOnDesktop ? 'none' : 'flex'};
      `)}
    `
  }};
`

const StyledTitle = styled.div`
  ${({
    theme: {
      typography: {
        headings: {
          small: { bundledCSS: headingSmallCSS },
        },
      },
      colors: { strokeBrand, textDefault },
      spacing: { spacing2, spacing8 },
    },
  }) => {
    return css`
      ${headingSmallCSS};
      text-align: left;
      text-transform: uppercase;
      border-left: ${spacing2} solid ${strokeBrand};
      color: ${textDefault};
      padding: 0 ${spacing8};
    `
  }};
`

const StyledEntityLink = styled(PrimaryCTABlink)`
  ${({
    theme: {
      spacing: { spacing40, spacing16 },
      colors: { staticNeutral600, staticNeutral800 },
      typography: {
        subheads: {
          small1: { bundledCSS: small1SubheadsCSS },
        },
      },
    },
  }) => {
    return css`
      display: flex;
      padding: 0 ${spacing16};
      height: ${spacing40};
      ${small1SubheadsCSS};
      background-color: ${staticNeutral800};
      max-width: 100%;
      @media (hover: hover) {
        &:hover:not([disabled]) {
          background-color: ${staticNeutral600};
        }
      }
    `
  }};
`

const EntitiesContainer = styled.div`
  ${({
    theme: {
      spacing: { spacing20, spacing8 },
    },
  }) => {
    return css`
      padding-top: ${spacing20};
      display: flex;
      gap: ${spacing8};
      flex-wrap: wrap;
      min-width: 0;
      width: 100%;
    `
  }}
`

const StyledEntityText = styled.div`
  ${({
    theme: {
      colors: { staticWhite },
      typography: {
        subheads: {
          small1: { bundledCSS: small1SubheadsCSS },
        },
      },
      utils: { ellipsis },
    },
  }) => {
    return css`
      color: ${staticWhite};
      ${small1SubheadsCSS};
      text-overflow: ellipsis;
      ${ellipsis('100%', 1)};
    `
  }};
`

const RelatedEntities: FunctionComponent<RelatedEntitiesProps> = ({
  hideOnDesktop,
  title,
  items,
}) => {
  const onImpressionTracking = useCallback<TrackingFnType>(
    () => ({
      event: 'element_impression',
      element: 'related_entities',
    }),
    [],
  )

  const onClick = useCallback<TrackingFnType>(
    () => ({
      event: 'element_click',
      element: 'related_entities',
    }),
    [],
  )
  const onImpression = useTracking(onImpressionTracking)
  const trackOnClick = useTracking(onClick)
  const viewportRef = useViewportTracking({
    onImpression,
    track: true,
  })
  const handleClick = useCallback(() => {
    trackOnClick()
  }, [trackOnClick])

  return (
    <Wrapper hideOnDesktop={hideOnDesktop} ref={viewportRef}>
      {title && <StyledTitle>{title}</StyledTitle>}
      <EntitiesContainer>
        {items.map(
          (item, index) =>
            item.title && (
              <StyledEntityLink
                key={item.link.href || index}
                title={item.title}
                href={item.link.href}
                onClick={handleClick}
              >
                {item.image && (
                  <EntityImage
                    url={item.image.src}
                    name={item.title}
                    size={24}
                  />
                )}
                <StyledEntityText>{item.title}</StyledEntityText>
              </StyledEntityLink>
            ),
        )}
      </EntitiesContainer>
    </Wrapper>
  )
}

export default RelatedEntities
